// src/pages/analytics/ReturnIndexPage/columns.js
import React from 'react';
import { Typography, Progress, Tooltip, Space, Tag } from 'antd';
import { 
    StarFilled, 
    InfoCircleOutlined, 
    WarningOutlined, 
    RocketOutlined, 
    UserOutlined, 
    StopOutlined 
} from '@ant-design/icons';

const { Text } = Typography;

// Configuration visuelle du profil / segmentation relationnelle
const RELATIONSHIP_CONFIG = {
    PARTNER_ACTIVE: {
        label: "Partenaire [B+]",
        tagColor: "gold",
        icon: <StarFilled />,
    },
    PARTNER_AT_RISK: {
        label: "Sous Vigilance [A-]",
        tagColor: "warning",
        icon: <WarningOutlined />,
    },
    KEY_PRESCRIBER: {
        label: "Prescripteur [B]",
        tagColor: "success",
        icon: <RocketOutlined />,
    },
    STANDARD: {
        label: "Standard [C]",
        tagColor: "default",
        icon: <UserOutlined />,
    },
    REFRACTORY: {
        label: "Non Réceptif [C-]",
        tagColor: "error",
        icon: <StopOutlined />,
    }
};

// Fonction utilitaire pour détecter le bon profil (robuste aux formats API)
const getCategoryKey = (record) => {
    const raw = String(record?.relationshipCategory || record?.prescriptionProfile || '').toUpperCase().trim();
    if (raw === 'REFRACTORY' || raw.includes('RÉCEPTIF') || raw.includes('RECEPTIF')) return 'REFRACTORY';
    if (raw === 'PARTNER_AT_RISK' || raw.includes('VIGILANCE')) return 'PARTNER_AT_RISK';
    if (raw === 'KEY_PRESCRIBER' || raw.includes('A+')) return 'KEY_PRESCRIBER';
    if (record?.isPartner || raw === 'PARTNER_ACTIVE') return 'PARTNER_ACTIVE';
    return 'STANDARD';
};

/**
 * @param {Function} onNameClick - La fonction callback pour ouvrir la modale
 */
export const returnIndexTableColumns = (onNameClick) => [
    {
        title: 'Médecin',
        dataIndex: 'physicianId',
        key: 'physicianName',
        width: 220,
        fixed: 'left',
        render: (text, record) => { 
            const firstName = record.physicianFirstName || '';
            const lastName = record.physicianLastName || '';
            const fullName = `${firstName} ${lastName}`.trim() || `ID: ${record.physicianId}`;
            const isPartner = record.isPartner;
            
            return (
                <Space>
                    {/* ÉTOILE SI PARTENAIRE */}
                    {isPartner && (
                        <Tooltip title={record.partnerNotes || "Médecin Partenaire"}>
                            <StarFilled style={{ color: '#fadb14', fontSize: '16px' }} />
                        </Tooltip>
                    )}
                    <a onClick={() => onNameClick(record.physicianId, record)}>
                        <Text strong style={{ 
                            color: isPartner ? '#856404' : '#1890ff',
                            textDecoration: 'underline', 
                            cursor: 'pointer' 
                        }}>
                            {fullName}
                        </Text>
                    </a>
                </Space>
            );
        },
        sorter: (a, b) => { 
            const nameA = `${a.physicianFirstName || ''} ${a.physicianLastName || ''}`.trim().toLowerCase();
            const nameB = `${b.physicianFirstName || ''} ${b.physicianLastName || ''}`.trim().toLowerCase();
            return nameA.localeCompare(nameB);
        },
    },
    // ⚡ NOUVELLE COLONNE : PROFIL DU MÉDECIN ⚡
    {
        title: 'Profil',
        key: 'relationshipCategory',
        width: 170,
        render: (_, record) => {
            const catKey = getCategoryKey(record);
            const conf = RELATIONSHIP_CONFIG[catKey] || RELATIONSHIP_CONFIG.STANDARD;
            
            return (
                <Tag 
                    color={conf.tagColor}
                    style={{ 
                        fontWeight: 'bold', 
                        fontSize: '11px', 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    {conf.icon} {conf.label.toUpperCase()}
                </Tag>
            );
        },
        sorter: (a, b) => getCategoryKey(a).localeCompare(getCategoryKey(b)),
    },
    {
        title: 'Spécialité',
        dataIndex: 'specialtyName',
        key: 'specialtyName',
        width: 180,
        render: (text) => text || <Text type="secondary">N/A</Text>,
        sorter: (a, b) => (a.specialtyName || '').localeCompare(b.specialtyName || ''),
    },
    {
        title: 'Région',
        dataIndex: 'regionName',
        key: 'regionName',
        width: 150,
        render: (text) => text || <Text type="secondary">N/A</Text>, 
        sorter: (a, b) => (a.regionName || '').localeCompare(b.regionName || ''),
    },
    {
        title: (
            <Tooltip title="Nombre de cycles où le médecin était une cible pour le délégué pendant la période sélectionnée.">
                Cible (Cycles) <InfoCircleOutlined />
            </Tooltip>
        ),
        dataIndex: 'timesTargetedInCycles',
        key: 'timesTargetedInCycles',
        align: 'center',
        width: 150,
        sorter: (a, b) => (a.timesTargetedInCycles ?? 0) - (b.timesTargetedInCycles ?? 0),
        render: (text) => text != null ? text : 0, 
    },
    {
        title: (
            <Tooltip title="Nombre de visites effectives rapportées par le délégué pour ce médecin sur la période.">
                Visites Effectives <InfoCircleOutlined />
            </Tooltip>
        ),
        dataIndex: 'effectiveVisitsReported',
        key: 'effectiveVisitsReported',
        align: 'center',
        width: 150,
        sorter: (a, b) => (a.effectiveVisitsReported ?? 0) - (b.effectiveVisitsReported ?? 0),
        render: (text) => text != null ? text : 0, 
    },
    {
        title: (
            <Tooltip title="Taux de réalisation : (Visites Effectives / Fois Cible) * 100">
                Indice Retour (%) <InfoCircleOutlined />
            </Tooltip>
        ),
        dataIndex: 'returnIndex',
        key: 'returnIndex',
        align: 'center',
        width: 200,
        sorter: (a, b) => (a.returnIndex ?? -1) - (b.returnIndex ?? -1),
        render: (percentage) => { 
            if (percentage === null || percentage === undefined) {
                return <Text type="secondary">N/A</Text>;
            }
            const percentValue = parseFloat(percentage.toFixed(1));
            let progressBarColor;
            let status = 'normal'; 
            if (percentValue < 66) {
                progressBarColor = '#ff4d4f'; status = 'exception';
            } else if (percentValue < 75) {
                progressBarColor = '#1677ff'; status = 'normal';
            } else {
                progressBarColor = '#52c41a'; status = 'success';
            }
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Progress
                        percent={percentValue}
                        status={status}
                        strokeColor={progressBarColor}
                        showInfo={false}
                        style={{ flexGrow: 1, marginRight: 8 }}
                    />
                    <Text style={{ color: progressBarColor, fontWeight: 'bold', minWidth: '45px', textAlign: 'right' }}>
                        {percentValue}%
                    </Text>
                </div>
            );
        },
    },
];